import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  getUserNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification
} from '@/utils/notificationsUtils';
import { Bell, CheckCircle, Trash, CheckCheck, ChefHat, Building, User, Cog } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Spinner } from '@/components/Spinner';
import { Badge } from '@/components/ui/badge';

interface Notification {
  id: string;
  title: string;
  message: string;
  created_at: string;
  is_read: boolean;
  user_id: string;
  type: string;
  related_id?: string;
  link_url?: string;
}

const UserNotifications = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    console.log("[UserNotifications] Component mounted, user:", user?.id);
    if (user) {
      loadNotifications();
    } else {
      console.log("[UserNotifications] No user available");
      setIsLoading(false);
    }
    
    return () => {
      console.log("[UserNotifications] Component unmounting");
    };
  }, [user]);

  const loadNotifications = async () => {
    console.log("[UserNotifications] Loading notifications for user:", user?.id);
    setIsLoading(true);
    setLoadError(null);
    
    try {
      if (user) {
        const userNotifications = await getUserNotifications(user.id);
        console.log("[UserNotifications] Loaded notifications:", userNotifications.length);
        setNotifications(userNotifications as Notification[]);
      }
    } catch (error) {
      console.error('[UserNotifications] Error loading notifications:', error);
      setLoadError("No se pudieron cargar las notificaciones");
      toast({
        title: "Error",
        description: "No se pudieron cargar las notificaciones",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    setIsUpdating(true);
    try {
      const success = await markNotificationAsRead(notificationId);
      if (success) {
        setNotifications(prev => 
          prev.map(notif => 
            notif.id === notificationId ? { ...notif, is_read: true } : notif
          )
        );
        toast({
          title: "Éxito",
          description: "Notificación marcada como leída"
        });
      }
    } catch (error) {
      console.error('[UserNotifications] Error marking as read:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!user) return;
    
    setIsUpdating(true);
    try {
      const success = await markAllNotificationsAsRead(user.id);
      if (success) {
        setNotifications(prev => 
          prev.map(notif => ({ ...notif, is_read: true }))
        );
      }
    } catch (error) {
      console.error('[UserNotifications] Error marking all as read:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteNotification = async (notificationId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    
    console.log('[UserNotifications] Deleting notification:', notificationId);
    setIsUpdating(true);
    
    try {
      const success = await deleteNotification(notificationId);
      if (success) {
        console.log('[UserNotifications] Successfully deleted, updating state');
        setNotifications(prev => 
          prev.filter(notif => notif.id !== notificationId)
        );
      } else {
        console.log('[UserNotifications] Delete operation returned false');
      }
    } catch (error) {
      console.error('[UserNotifications] Error deleting notification:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      handleMarkAsRead(notification.id);
    }
    
    if (notification.link_url) {
      navigate(notification.link_url);
    }
  };

  const getTimeAgo = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const secondsAgo = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (secondsAgo < 60) return `Hace ${secondsAgo} segundos`;
    if (secondsAgo < 3600) return `Hace ${Math.floor(secondsAgo / 60)} minutos`;
    if (secondsAgo < 86400) return `Hace ${Math.floor(secondsAgo / 3600)} horas`;
    return `Hace ${Math.floor(secondsAgo / 86400)} días`;
  };

  const getUnreadCount = () => {
    const unreadCount = notifications.filter(notif => !notif.is_read).length;
    console.log('[UserNotifications] Unread count:', unreadCount, 'out of', notifications.length);
    return unreadCount;
  };

  const getNotificationStyling = (type: string) => {
    switch (type) {
      case 'recipe':
        return {
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          textColor: 'text-green-800',
          icon: <ChefHat className="h-5 w-5 text-green-600" />
        };
      case 'admin':
        return {
          bgColor: 'bg-pink-50',
          borderColor: 'border-pink-200',
          textColor: 'text-pink-800',
          icon: <Building className="h-5 w-5 text-pink-600" />
        };
      case 'user':
        return {
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          textColor: 'text-blue-800',
          icon: <User className="h-5 w-5 text-blue-600" />
        };
      case 'system':
        return {
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          textColor: 'text-yellow-800',
          icon: <Cog className="h-5 w-5 text-yellow-600" />
        };
      default:
        return {
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200',
          textColor: 'text-gray-800',
          icon: <Bell className="h-5 w-5 text-gray-600" />
        };
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Spinner size="lg" />
        <span className="ml-2">Cargando notificaciones...</span>
      </div>
    );
  }

  if (loadError) {
    return (
      <Card className="w-full">
        <CardContent className="py-6">
          <div className="text-center text-red-500">
            <p>{loadError}</p>
            <Button 
              variant="outline" 
              className="mt-4" 
              onClick={loadNotifications}
            >
              Intentar de nuevo
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center gap-2">
          <Bell className="text-vegana-primary h-5 w-5" />
          <CardTitle>Mis Notificaciones</CardTitle>
          {getUnreadCount() > 0 && (
            <Badge className="ml-2 bg-vegana-primary">{getUnreadCount()}</Badge>
          )}
        </div>
        {notifications.length > 0 && (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleMarkAllAsRead}
            disabled={isUpdating || notifications.every(n => n.is_read)}
            className="flex items-center gap-1"
          >
            <CheckCheck className="h-4 w-4" />
            <span className="hidden sm:inline">Marcar todas como leídas</span>
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {notifications.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No tienes notificaciones aún
          </div>
        ) : (
          <div className="space-y-3">
            {notifications.map((notification) => {
              const styling = getNotificationStyling(notification.type);
              const hasLink = !!notification.link_url;
              
              return (
                <div 
                  key={notification.id} 
                  className={`p-3 rounded-lg border ${styling.borderColor} ${styling.bgColor} ${!notification.is_read ? 'shadow-sm' : ''} ${hasLink ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
                  onClick={hasLink ? () => handleNotificationClick(notification) : undefined}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-2">
                      {styling.icon}
                      <h3 className={`font-medium ${notification.is_read ? 'text-gray-700' : styling.textColor}`}>
                        {notification.title}
                      </h3>
                    </div>
                    <div className="flex gap-2">
                      {!notification.is_read && (
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-7 w-7" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkAsRead(notification.id);
                          }}
                          disabled={isUpdating}
                        >
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        </Button>
                      )}
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-7 w-7" 
                        onClick={(e) => handleDeleteNotification(notification.id, e)}
                        disabled={isUpdating}
                      >
                        <Trash className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm mt-1 text-gray-600">{notification.message}</p>
                  <div className="mt-2 text-xs text-gray-500 flex justify-between">
                    <span>{getTimeAgo(notification.created_at)}</span>
                    <div className="flex items-center gap-2">
                      {hasLink && <span className="text-blue-500">Ver publicación</span>}
                      {notification.is_read && <span>Leída</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
      <CardFooter className="flex justify-center pt-0">
        <Button 
          variant="outline"
          onClick={loadNotifications}
          disabled={isLoading || isUpdating}
          className="w-full sm:w-auto"
        >
          {isLoading ? <Spinner size="sm" /> : 'Actualizar notificaciones'}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default UserNotifications;
